import type { Ticket } from './types';

export type TicketCancelState = 'none' | 'quoted' | 'processing' | 'partial' | 'cancelled';

export function getTicketCancelState(ticket: Ticket | undefined): TicketCancelState {
  if (!ticket) return 'none';

  const rawState = ticket.cancellation?.state;
  const topLevelCancelled = ticket.status === 'CANCELLED';
  const nestedCancelled = ticket.cancellation?.status === 'CANCELLED';

  // A rider-facing preview must stay visible if the backend explicitly marks it as quoted,
  // even if the broader order record has already been stamped CANCELLED.
  if (rawState === 'quoted') {
    return 'quoted';
  }

  if (rawState === 'cancelled') {
    return 'cancelled';
  }

  if (rawState === 'partial') {
    return 'partial';
  }

  // Terminal cancellation must win over stale in-flight state carried alongside it.
  // The backend can keep `cancellation.state = 'processing'` during convergence while the
  // final lifecycle is already `CANCELLED`; showing “in progress” after the order is terminal
  // is a false-positive and hides the fact the QR is no longer valid.
  if (topLevelCancelled || nestedCancelled) {
    return 'cancelled';
  }

  if (rawState === 'processing') {
    return 'processing';
  }

  if (ticket.cancellation?.status === 'SOFT_CANCEL' || ticket.status === 'SOFT_CANCEL') {
    return 'quoted';
  }

  return 'none';
}

export function isBusTicket(ticket: Ticket | undefined): boolean {
  if (!ticket) return false;

  return (
    ticket.journey.vehicleCategory === 'BUS' ||
    ticket.journey.variant === 'AC' ||
    ticket.journey.variant === 'NON_AC' ||
    ticket.passes.some(
      (pass) =>
        pass.journey.vehicleCategory === 'BUS' || Boolean(pass.authType || pass.vehicleNumber)
    )
  );
}
